import React, { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Loader } from '@domain-group/fe-blueprint-ui/loader';

import { handleError } from '@domain-group/fe-helper';

import mapReviews from '../../utils/map-recommendation-reviews';
import ReviewList from '../shared/recommendations/reviews-list';

import type { ContactByAgencyIdAndAgentIdPaginationQuery } from '../../generated/graphql';
import type { RecommendationPayload, TrackingCallbacks } from '../../@types';
import { useReviewsContext } from '../recommendations-enquiry-section';

export type ContactRecommendationsWithQueryProps = TrackingCallbacks & {
  agentIdV2: string;
  agencyId: number;
  starRatingsEnabled: boolean;
};

export const ContactRecommendationsByAgencyIdQuery = gql`
  query contactByAgencyIdAndAgentIdPagination(
    $agentIdV2: String!
    $agencyId: Int!
    $pageSize: Int
    $pageNumber: Int
  ) {
    contactByAgencyIdAndAgentId(agentIdV2: $agentIdV2, agencyId: $agencyId) {
      recommendations(pageSize: $pageSize, pageNumber: $pageNumber) {
        agentProfileUrl
        reviews {
          title
          description
          reviewedOn
          reviewType
          starRating
          isRecommended
          agentReviewTrackingPixelUrl
          agencyReviewTrackingPixelUrl
          bedrooms
          bathrooms
          carparks
          streetAddress
          suburb
          state
          postCode
        }
      }
    }
  }
`;

export const ContactRecommendationsWithStarRatingsAndQuery = ({
  agentIdV2,
  agencyId,
  onPageRendered,
  onVendorRecoViewAll,
  onVendorRecoRendered,
  onVendorRecoImpression,
  onVendorRecoModalViewMore,
  onVendorRecoShowMore,
  starRatingsEnabled,
}: ContactRecommendationsWithQueryProps): JSX.Element | null => {
  const { setHasReviews, reviewPageNumber, reviewPageSize } =
    useReviewsContext();

  const { loading, data, error } =
    useQuery<ContactByAgencyIdAndAgentIdPaginationQuery>(
      ContactRecommendationsByAgencyIdQuery,
      {
        variables: {
          agentIdV2,
          agencyId,
          pageSize: reviewPageSize,
          pageNumber: reviewPageNumber,
        },
      },
    );

  const reviews = data?.contactByAgencyIdAndAgentId?.recommendations?.reviews
    ?.length
    ? mapReviews(data.contactByAgencyIdAndAgentId.recommendations.reviews)
    : [];

  useEffect(() => {
    setHasReviews(Boolean(reviews.length));
  }, [reviews.length, setHasReviews]);

  if (loading) {
    return <Loader message="Loading reviews..." />;
  }

  if (error) {
    handleError(error);
    return (
      <div data-testid="fe-faa-contact-recommendations-with-star-ratings-error" />
    );
  }

  if (reviews.length) {
    return (
      <div data-testid="fe-faa-contact-recommendations-with-star-ratings-container">
        <ReviewList
          profileUrl={
            data?.contactByAgencyIdAndAgentId?.recommendations
              ?.agentProfileUrl || ''
          }
          reviews={reviews}
          starRatingsEnabled={starRatingsEnabled}
          onPageRendered={() => {
            onPageRendered(
              data?.contactByAgencyIdAndAgentId as RecommendationPayload,
            );
          }}
          onVendorRecoViewAll={() => {
            onVendorRecoViewAll(
              data?.contactByAgencyIdAndAgentId as RecommendationPayload,
            );
          }}
          onVendorRecoRendered={() => {
            onVendorRecoRendered(
              data?.contactByAgencyIdAndAgentId as RecommendationPayload,
            );
          }}
          onVendorRecoImpression={() => {
            onVendorRecoImpression(
              data?.contactByAgencyIdAndAgentId as RecommendationPayload,
            );
          }}
          onVendorRecoModalViewMore={() => {
            onVendorRecoModalViewMore(
              data?.contactByAgencyIdAndAgentId as RecommendationPayload,
            );
          }}
          onVendorRecoShowMore={() => {
            onVendorRecoShowMore?.(
              data?.contactByAgencyIdAndAgentId as RecommendationPayload,
            );
          }}
        />
      </div>
    );
  }

  return <></>;
};
